// The item windows (inventory, character sheet, crafting, loot bags, shops and trade): Pixi widgets rebuilt whenever the session changes, with the rules and tooltips from the client core.
import { app } from '../app';
import { api, content, itemsById, Container } from '../api';
import { Widget, focus, type UiPointerEvent, Checkbox, widget, fill, rowIcon, itemIcon, characterIcon, moneyView, formatMoney } from '../ui/compose';
import { hoverTip, hideTip, itemTip, simpleTip, type TipData } from '../ui/tooltips';
import type { Windows } from './windows';

export interface Stack { item: number; count: number; bound: boolean }
export interface InventoryData { main: Stack[]; belt: Stack[]; armor: Stack[]; money: number; armorValue: number; cooldown: number; cooldownTotal: number; ghosts: number[]; armorGhosts: number[] }
export interface Slot { c: number; i: number }

const click = () => app.audio.play('sfx_ui_click', 0.5, 0);

export function button(text: string, onclick: () => void, disabled = false, cls = ''): Widget {
  return widget('button', { class: `button ${cls}`, disabled, onclick: (e: UiPointerEvent) => { e.stopPropagation(); click(); onclick(); } }, text);
}

/** A window's title row: an optional icon (a skin icon's name: 'bag'), the title, its hotkey, and a close button. */
export function titleBar(title: string, closeHint: string, onClose: () => void, color = 'var(--title)', icon = ''): Widget {
  return widget('box', { class: 'window-title' },
    icon ? widget('box', { class: 'icon16 title-icon', style: { backgroundImage: `var(--img-${icon})` } }) : null,
    widget('text', { style: { color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, title),
    widget('text', { class: 'hotkey' }, closeHint), closeButton(onClose));
}

/** A window's close button: a pixel-art cross centred in it (a font's "×" sits low and overflows a button this small). */
export function closeButton(onClose: () => void): Widget {
  return widget('button', { class: 'button close', title: 'Close', onclick: (e: UiPointerEvent) => { e.stopPropagation(); click(); onClose(); } },
    widget('box', { class: 'close-icon', style: { backgroundImage: 'var(--img-close)' } }));
}

/** A stack in a slot: quality frame (Uncommon and up), icon, count. */
export function slotEl(stack: Stack | null, opts: { alpha?: number; ghost?: number; size?: number } = {}): Widget {
  const size = opts.size ?? 22;
  const el = widget('box', { class: 'slot', style: { width: `${size}px`, height: `${size}px` } });
  if (stack && stack.item && stack.count > 0) {
    const def = itemsById.get(stack.item);
    if (def && def.quality >= 2) el.append(widget('box', { class: 'quality', style: { borderColor: def.qualityColor } }));
    const icon = itemIcon(stack.item, 16);
    icon.classList.add('icon');
    if (opts.alpha !== undefined) icon.style.opacity = String(opts.alpha);
    el.append(icon);
    if (stack.count > 1) el.append(widget('box', { class: 'count' }, String(stack.count)));
  } else if (opts.ghost) {
    const icon = itemIcon(opts.ghost, 16);
    icon.classList.add('icon', 'ghost');
    el.append(icon);
  }
  return el;
}

// =============================================================================================================
// inventory
// =============================================================================================================

export class InventoryWindow {
  readonly el = widget('box', { class: 'panel window' });
  isOpen = false;
  data: InventoryData | null = null;
  /** Shift-click sends the stack here (into an open bag, the shop, the trade) instead of equipping. */
  shiftTarget: ((c: number, i: number) => void) | null = null;
  shiftHint: ((s: Stack) => string) | null = null;
  hovered: Slot | null = null;
  private cooldownShades: { el: Widget }[] = [];
  private renderKey = '';

  constructor(private readonly w: Windows) { }

  stack(c: number, i: number): Stack | null {
    const d = this.data;
    if (!d) return null;
    return (c === Container.Main ? d.main : c === Container.Belt ? d.belt : d.armor)[i] ?? null;
  }

  render(): void {
    const d = this.data = api.inventory() as InventoryData;
    const held = this.w.held;
    const key = JSON.stringify([api.contentVersion(), d.main, d.belt, d.armor, d.money, held, this.w.confirming, !!this.shiftTarget]);
    if (key === this.renderKey) { this.updateCooldown(); return; }
    this.renderKey = key;
    const grid = widget('box', { class: 'grid', style: { gridTemplateColumns: `repeat(${content.bagColumns}, auto)` } });
    this.cooldownShades = [];
    d.main.forEach((s, i) => {
      const isHeld = held?.c === Container.Main && held.i === i;
      const el = slotEl(s, { alpha: isHeld ? 0.35 : 1 });
      if (held && !isHeld && !api.canPlace(held.c, held.i, Container.Main, i)) el.style.filter = 'sepia(1) hue-rotate(-40deg) saturate(3)';
      const def = itemsById.get(s.item);
      if (def?.consumable) {
        const shade = widget('box', { class: 'cooldown', style: { height: `${(d.cooldown / d.cooldownTotal) * 100}%` } });
        el.append(shade);
        this.cooldownShades.push({ el: shade });
      }
      el.addEventListener('mousedown', e => this.slotClick(e, Container.Main, i));
      el.addEventListener('contextmenu', e => {
        e.preventDefault();
        if (!this.w.held && api.useItem(Container.Main, i)) app.audio.play('sfx_drink', 0.8, 0.05);
      });
      el.addEventListener('mouseenter', () => { this.hovered = { c: Container.Main, i }; });
      el.addEventListener('mouseleave', () => { if (this.hovered?.i === i) this.hovered = null; });
      hoverTip(el, () => this.w.held || this.w.confirming ? null : s.count ? itemTip(s.item, { bound: s.bound, count: s.count, hint: this.hint(s), compare: true, shift: this.w.game.shiftHeld }) : null);
      grid.append(el);
    });
    const used = d.main.filter(x => x.count > 0).length;
    fill(this.el,
      titleBar('Inventory', 'B', () => this.w.closeInventory(), undefined, 'bag'),
      widget('box', { class: 'well' }, grid),
      widget('box', { class: 'well row', style: { padding: `3px 4px` } }, moneyView(d.money), widget('text', { class: 'grow' }),
        widget('text', { class: 'dim small' }, held ? 'Click outside to drop' : 'Q drop'),
        widget('text', { class: 'dim' }, `${used} / ${d.main.length}`)),
    );
  }

  private hint(s: Stack): string {
    const def = itemsById.get(s.item);
    if (!def) return '';
    let hint = this.shiftTarget ? this.shiftHint?.(s) ?? 'Shift-click to put in bag'
      : def.tool !== 'none' || def.armor >= 0 || def.offhand !== 'none' || def.jewelry !== 'none' ? 'Shift-click to equip' : def.consumable ? 'Right-click to drink' : '';
    if (s.bound && !hint) hint = 'Soulbound: Q to destroy it';
    return hint;
  }

  private slotClick(e: UiPointerEvent, c: number, i: number): void {
    if (e.button !== 0 || this.w.confirming) return;
    e.stopPropagation();
    const held = this.w.held, stack = this.stack(c, i);
    if (held) {
      if (held.c !== c || held.i !== i) this.w.requestMove(held.c, held.i, c, i);
      this.w.setHeld(null);
    } else if (stack && stack.count > 0) {
      if (e.shiftKey && this.shiftTarget) this.shiftTarget(c, i);
      else if (e.shiftKey) this.w.requestQuickMove(c, i);
      else this.w.setHeld({ c, i });
    }
  }

  /** Q drops one of the hovered stack; Ctrl+Q all of it. */
  dropHovered(all: boolean): void {
    if (!this.hovered || this.w.held) return;
    const s = this.stack(this.hovered.c, this.hovered.i);
    if (s && s.count > 0) this.w.dropOrDestroy(this.hovered.c, this.hovered.i, all ? s.count : 1);
  }

  /** The cooldown shade drains without a server update. */
  tick(dt: number): void {
    const d = this.data;
    if (!d || d.cooldown <= 0 || !this.cooldownShades.length) return;
    d.cooldown = Math.max(0, d.cooldown - dt);
    this.updateCooldown();
  }
  private updateCooldown(): void {
    const d = this.data;
    if (!d) return;
    for (const { el } of this.cooldownShades) el.style.height = `${d.cooldownTotal > 0 ? d.cooldown / d.cooldownTotal * 100 : 0}%`;
  }
}

// =============================================================================================================
// character sheet
// =============================================================================================================

interface CharacterData {
  name: string; appearance: number; level: number; exp: number; next: number; hp: number; maxHp: number; mana: number; maxMana: number; armor: number;
  unspent: number; spent: number; healthTip: TipData; manaTip: TipData; armorTip: TipData; talentTip: TipData;
}

export class CharacterWindow {
  private renderKey = '';
  readonly el = widget('box', { class: 'panel window' });
  isOpen = false;

  constructor(private readonly w: Windows) { }

  render(): void {
    const d = api.character() as CharacterData;
    const inv = api.inventory() as InventoryData;
    const held = this.w.held;
    const key = JSON.stringify([api.contentVersion(), d, inv.belt, inv.armor, inv.ghosts, inv.armorGhosts, held, this.w.inventory.isOpen, this.w.confirming]);
    if (key === this.renderKey) return;
    this.renderKey = key;
    const slot = (c: number, i: number, label: string, ghost?: number) => {
      const stack = (c === Container.Armor ? inv.armor : inv.belt)[i];
      const isHeld = held?.c === c && held.i === i;
      const el = slotEl(stack, { alpha: isHeld ? 0.35 : 1, ghost });
      if (held && !isHeld && !api.canPlace(held.c, held.i, c, i)) el.style.filter = 'sepia(1) hue-rotate(-40deg) saturate(3)';
      el.addEventListener('mousedown', e => {
        if (e.button !== 0 || this.w.confirming) return;
        e.stopPropagation();
        const h0 = this.w.held;
        if (h0) {
          if (h0.c !== c || h0.i !== i) this.w.requestMove(h0.c, h0.i, c, i);
          this.w.setHeld(null);
        } else if (stack.count > 0) {
          if (e.shiftKey || !this.w.inventory.isOpen) api.quickMove(c, i); // back to the bag
          else this.w.setHeld({ c, i });
        }
      });
      hoverTip(el, () => {
        if (this.w.held || this.w.confirming) return null;
        if (stack.count > 0) return itemTip(stack.item, { bound: stack.bound, hint: this.w.inventory.isOpen ? 'Click to pick up, shift-click to unequip' : 'Click to unequip' });
        const isOffhand = c === Container.Belt && i === offhand;
        if (isOffhand && !offhandTakes) return simpleTip('Offhand', [[weaponClass ? 'A two-handed weapon needs both hands.' : 'Equip a weapon first: what goes here depends on it.', 'var(--desc)']]);
        const what = c === Container.Armor ? `${content.armorSlots[i]} armour` : isOffhand ? offhandTakes : content.beltSlots[i] === 'Weapon' ? 'A weapon' : `A ${content.beltSlots[i].replace(/ \d+$/, '').toLowerCase()}`;
        return simpleTip('Empty', [[`${what} ${isOffhand && offhandTakes === 'Arrows' ? 'go' : 'goes'} here.`, 'var(--desc)'], [this.w.inventory.isOpen ? 'Pick one up in your bag, then click here' : 'Open your bag (B) to equip', 'var(--hint)']]);
      });
      el.title = label;
      return el;
    };
    // Gathering tools sit in a row on top; jewellery stacks above the weapon, which has its offhand slot below it (taking what the weapon uses).
    const beltSlot = (name: string) => content.beltSlots.indexOf(name);
    const offhand = beltSlot('Offhand'), weaponClass = itemsById.get(inv.belt[beltSlot('Weapon')]?.item ?? 0)?.weapon?.class ?? '';
    const offhandTakes = ({ staff: 'A spell scroll', bow: 'Arrows', onehanded: 'A shield' } as Record<string, string>)[weaponClass] ?? '';
    const tools = widget('box', { class: 'row', style: { justifyContent: 'center' } },
      ...['Axe', 'Pickaxe', 'Net'].map(n => slot(Container.Belt, beltSlot(n), n, inv.ghosts[beltSlot(n)])));
    const left = widget('box', { class: 'col' }, ...content.armorSlots.map((n, i) => slot(Container.Armor, i, n, inv.armorGhosts[i])));
    const offhandSlot = slot(Container.Belt, offhand, 'Offhand', inv.ghosts[offhand]);
    if (!offhandTakes && !inv.belt[offhand]?.count) offhandSlot.style.opacity = '0.45';
    const right = widget('box', { class: 'col', style: { justifyContent: 'center' } },
      ...['Necklace', 'Ring 1', 'Ring 2', 'Weapon'].map(n => slot(Container.Belt, beltSlot(n), n, inv.ghosts[beltSlot(n)])), offhandSlot);
    const figure = characterIcon(d.appearance, 48, 0, 0, inv.armor.map(a => a.item));
    // A line of the sheet: a name, its value on the right; hovering explains it.
    const line = (name: string, value: string, color: string, tip: TipData | null) => {
      const el = widget('box', { class: 'row', style: { minHeight: `13px` } },
        widget('text', null, name), widget('text', { class: 'grow' }), widget('text', { class: 'strong', style: { color } }, value));
      if (tip) hoverTip(el, () => tip);
      return el;
    };
    const sheet = widget('box', { class: 'well col', style: { minWidth: `104px`, gap: `2px` } },
      line('Level', String(d.level), 'var(--title)', null),
      line('Experience', d.next > 0 ? `${d.exp.toLocaleString()} / ${d.next.toLocaleString()}` : 'max', 'var(--dim)', null),
      widget('box', { class: 'rule' }),
      line('Health', `${d.hp} / ${d.maxHp}`, '#ec7a70', d.healthTip),
      line('Mana', `${d.mana} / ${d.maxMana}`, '#7aa8ec', d.manaTip),
      line('Armor', String(d.armor), '#aac4e0', d.armorTip),
      widget('box', { class: 'rule' }),
      line('Talents', d.unspent > 0 ? `${d.unspent} to spend` : `${d.spent} learned`, d.unspent > 0 ? 'var(--good)' : 'var(--text)', d.talentTip),
      button(d.unspent > 0 ? 'Talent tree (N) *' : 'Talent tree (N)', () => this.w.toggleTalents(), this.w.confirming));
    const figureBox = widget('box', { style: { padding: `2px` } }, figure);
    fill(this.el,
      titleBar(d.name, 'C', () => this.w.closeCharacter(), undefined, 'character'),
      widget('box', { class: 'row', style: { alignItems: 'stretch' } },
        widget('box', { class: 'well col', style: { alignItems: 'center' } },
          tools, widget('box', { class: 'rule', style: { alignSelf: 'stretch' } }),
          widget('box', { class: 'row', style: { alignItems: 'center' } }, left, figureBox, right)),
        sheet),
    );
    this.el.addEventListener('mousedown', e => e.stopPropagation());
  }
}

// =============================================================================================================
// workbench
// =============================================================================================================

interface RecipeData { id: number; item: number; count: number; name: string; tab: string; set: string | null; max: number; inputs: { item: number; count: number; have: number; name: string }[] }

export class CraftingWindow {
  readonly el = widget('box', { class: 'panel window', style: { width: `300px` } });
  station = 0;
  private tab = 'All';
  private canOnly = false;
  private selected: number | null = null;
  private quantity = 1;
  private readonly openSets = new Set<string>();
  private listScroll = 0;

  constructor(private readonly w: Windows) { }

  get isOpen(): boolean { return this.station !== 0; }

  open(station: number): void { this.station = station; this.listScroll = 0; }
  close(): void { this.station = 0; }

  render(): boolean {
    const d = api.crafting(this.station) as { gone: boolean; name?: string; recipes?: RecipeData[] };
    if (d.gone || !d.recipes) return false;
    const all = d.recipes;
    // Only the tabs this station has recipes for (a furnace only smelts materials).
    const tabNames = ['All', ...['Materials', 'Tools', 'Armour'].filter(t => all.some(r => r.tab === t))];
    if (!tabNames.includes(this.tab)) this.tab = 'All';
    const shown = (r: RecipeData) => (this.tab === 'All' || r.tab === this.tab) && (!this.canOnly || r.max > 0);
    if (this.selected === null || !all.some(r => r.id === this.selected && shown(r))) {
      this.selected = [...all.filter(shown)].sort((a, b) => (b.max > 0 ? 1 : 0) - (a.max > 0 ? 1 : 0))[0]?.id ?? null;
      this.quantity = 1;
    }
    const tabs = widget('box', { class: 'tabs' }, ...tabNames.map(t =>
      button(t, () => { this.tab = t; this.w.refresh(); }, false, t === this.tab ? 'active' : '')));
    const filter = new Checkbox({ checked: this.canOnly });
    filter.textContent = this.canOnly ? '[x] Can craft' : '[ ] Can craft';
    filter.addEventListener('input', () => { this.canOnly = filter.checked; this.w.refresh(); });

    // The list: armour of one look folds into a set row.
    const list = widget('box', { class: 'col scroll', style: { width: `120px`, height: `170px`, background: 'rgba(0,0,0,0.22)', gap: '0' } });
    const seenSets = new Set<string>();
    const row = (r: RecipeData, indented: boolean) => {
      const def = itemsById.get(r.item);
      const color = def?.qualityColor ?? '#fff';
      const icon = itemIcon(r.item, rowIcon());
      icon.style.opacity = r.max > 0 ? '1' : '0.5';
      const name = indented && r.set ? (def?.name ?? r.name).slice(r.set.length + 1) : r.name;
      const el = widget('box', { class: 'row', style: { padding: `1px 2px 1px ${indented ? 10 : 2}px`, cursor: 'pointer',
        background: r.id === this.selected ? 'rgba(249,146,60,0.25)' : '' }, onclick: () => { this.selected = r.id; this.quantity = 1; this.w.refresh(); } },
        icon, widget('text', { class: 'grow', style: { color, opacity: r.max > 0 ? '1' : '0.5' } }, name),
        r.max > 0 ? widget('text', { class: 'good' }, String(r.max)) : null);
      hoverTip(icon, () => itemTip(r.item, { count: r.count, hint: 'Click to see the recipe', compare: true, shift: this.w.game.shiftHeld }));
      return el;
    };
    for (const r of all.filter(shown)) {
      if (!r.set) { list.append(row(r, false)); continue; }
      if (seenSets.has(r.set)) continue;
      seenSets.add(r.set);
      const set = r.set, open = this.openSets.has(set);
      const can = all.filter(x => x.set === set && x.max > 0).length;
      list.append(widget('box', { class: 'row', style: { padding: `2px`, cursor: 'pointer' },
        onclick: () => { if (!this.openSets.delete(set)) this.openSets.add(set); this.w.refresh(); } },
        widget('text', { class: 'grow' }, `${open ? '-' : '+'} ${set} set`), can ? widget('text', { class: 'good' }, String(can)) : null));
      if (open) for (const p of all.filter(x => shown(x) && x.set === set)) list.append(row(p, true));
    }
    if (!list.widgets.length) list.append(widget('box', { class: 'dim small center' }, this.canOnly ? 'Nothing you can craft yet.' : 'No recipes here.'));

    // The selected recipe.
    const detail = widget('box', { class: 'col grow', style: { minHeight: `110px` } });
    const r = all.find(x => x.id === this.selected);
    if (r) {
      const def = itemsById.get(r.item);
      this.quantity = Math.max(1, Math.min(this.quantity, Math.max(1, r.max)));
      const big = slotEl({ item: r.item, count: r.count, bound: false });
      hoverTip(big, () => itemTip(r.item, { count: r.count, compare: true, shift: this.w.game.shiftHeld }));
      const needs = r.inputs.map(i => {
        const line = widget('box', { class: 'row' }, itemIcon(i.item, rowIcon()), widget('text', { class: 'grow' }, i.name),
          widget('text', { style: { color: i.have >= i.count ? 'var(--good)' : 'var(--error)' } }, `${i.have}/${i.count}`));
        hoverTip(line, () => itemTip(i.item, { count: i.count, compare: true, shift: this.w.game.shiftHeld }));
        return line;
      });
      const qty = widget('text', { style: { minWidth: `20px`, textAlign: 'center' } }, String(this.quantity));
      detail.append(
        widget('box', { class: 'row' }, big, widget('box', { class: 'col', style: { gap: `2px` } },
          widget('text', { style: { color: def?.qualityColor } }, def?.name ?? r.name), widget('text', { class: 'small dim' }, `Makes ${r.count}`))),
        widget('box', { class: 'accent' }, 'Needs'), ...needs, widget('box', { class: 'grow' }),
        widget('box', { class: 'row' },
          button('-', () => { this.quantity--; this.w.refresh(); }, this.quantity <= 1), qty,
          button('+', () => { this.quantity++; this.w.refresh(); }, this.quantity >= r.max),
          button('Max', () => { this.quantity = r.max; this.w.refresh(); }, r.max <= 1 || this.quantity >= r.max),
          widget('text', { class: 'grow' }),
          button(this.quantity > 1 ? `Craft ${this.quantity}` : 'Craft', () => {
            api.craft(r.id, Math.min(this.quantity, r.max), this.station);
            app.audio.play('sfx_craft', 0.7, 0.05);
            this.quantity = 1;
          }, r.max <= 0)));
    }
    const oldScroll = (this.el.querySelector('.scroll') as Widget | null)?.scrollTop ?? this.listScroll;
    fill(this.el, titleBar(d.name ?? 'Workbench', 'E', () => this.w.closeStation()),
      widget('box', { class: 'row' }, tabs, widget('text', { class: 'grow' }), filter),
      widget('box', { class: 'row', style: { alignItems: 'stretch', gap: `8px` } }, list, detail));
    list.scrollTop = this.listScroll = oldScroll;
    return true;
  }
}

// =============================================================================================================
// loot bag
// =============================================================================================================

export class BagWindow {
  readonly el = widget('box', { class: 'panel window' });
  bag = 0;
  private page = 0;

  constructor(private readonly w: Windows) { }

  get isOpen(): boolean { return this.bag !== 0; }
  open(id: number): void { this.bag = id; this.page = 0; }
  close(): void { this.bag = 0; }

  render(): boolean {
    const d = api.bag(this.bag) as { gone: boolean; contents?: Stack[] };
    if (d.gone || !d.contents) return false;
    const per = 10, contents = d.contents, pages = Math.max(1, Math.ceil(contents.length / per));
    this.page = Math.max(0, Math.min(this.page, pages - 1));
    const first = this.page * per, onPage = contents.slice(first, first + per);
    const grid = widget('box', { class: 'grid', style: { gridTemplateColumns: 'repeat(5, auto)' } });
    onPage.forEach((s, k) => {
      const el = slotEl(s);
      el.addEventListener('mousedown', e => { if (e.button === 0) { e.stopPropagation(); api.bagTake(this.bag, first + k, s.item); } });
      hoverTip(el, () => itemTip(s.item, { bound: s.bound, count: s.count, hint: 'Click to take', compare: true, shift: this.w.game.shiftHeld }));
      grid.append(el);
    });
    if (!onPage.length) grid.append(slotEl(null));
    fill(this.el, titleBar('Bag', 'E', () => this.w.closeStation()), grid,
      widget('box', { class: 'row' },
        pages > 1 ? button('<', () => { this.page--; this.w.refresh(); }, this.page === 0) : null,
        pages > 1 ? widget('text', null, `${this.page + 1}/${pages}`) : null,
        pages > 1 ? button('>', () => { this.page++; this.w.refresh(); }, this.page >= pages - 1) : null,
        widget('text', { class: 'grow' }), button('Take all', () => api.bagTakeAll(this.bag), contents.length === 0)),
      contents.length === 0 ? widget('box', { class: 'dim center' }, 'Empty') : null);
    return true;
  }
}

// =============================================================================================================
// shop
// =============================================================================================================

export class VendorWindow {
  readonly el = widget('box', { class: 'panel window' });
  npc = 0;
  private page = 0;

  constructor(private readonly w: Windows) {
    // A stack picked up in the inventory and dropped on the shop is sold.
    this.el.addEventListener('mousedown', e => {
      if (e.button !== 0 || !this.w.held) return;
      e.stopPropagation();
      const held = this.w.held;
      this.w.setHeld(null);
      this.sell(held.c, held.i);
    });
  }

  get isOpen(): boolean { return this.npc !== 0; }
  open(npc: number): void { this.npc = npc; this.page = 0; }
  close(): void { this.npc = 0; }

  sell(c: number, i: number): void {
    if (api.vendorSellChecked(this.npc, c, i)) app.audio.play('sfx_coins', 0.7, 0.08);
  }

  sellHint(s: Stack): string {
    return (itemsById.get(s.item)?.sellPrice ?? 0) > 0 ? 'Shift-click to sell' : 'The vendor won\'t buy this';
  }

  render(): boolean {
    const d = api.vendor(this.npc) as { gone: boolean; name?: string; money: number; junk: number; maxBuy: number; stock?: { item: number; price: number; priceText: string }[] };
    if (d.gone || !d.stock) return false;
    const per = 8, pages = Math.max(1, Math.ceil(d.stock.length / per));
    this.page = Math.max(0, Math.min(this.page, pages - 1));
    const grid = widget('box', { class: 'grid', style: { gridTemplateColumns: 'repeat(2, auto)', gap: `3px` } });
    for (let k = 0; k < per; k++) {
      const index = this.page * per + k, offer = d.stock[index];
      const cell = widget('box', { class: 'slot row', style: { width: `104px`, height: `24px`, gap: `3px` } });
      grid.append(cell);
      if (!offer) { cell.style.opacity = '0.5'; continue; }
      const def = itemsById.get(offer.item);
      const afford = d.money >= offer.price;
      cell.style.cursor = 'pointer';
      cell.append(slotEl({ item: offer.item, count: 1, bound: false }, { alpha: afford ? 1 : 0.5, size: 18 }),
        widget('box', { class: 'col grow', style: { gap: `1px`, overflow: 'hidden' } },
          widget('box', { style: { color: def?.qualityColor, opacity: afford ? '1' : '0.55', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: '..' } }, def?.name ?? '?'),
          widget('box', { class: 'row small' }, moneyView(offer.price), afford ? null : widget('text', { class: 'error', style: { marginLeft: 'auto' } }, 'too dear'))));
      cell.addEventListener('mousedown', e => {
        if (e.button !== 0 || this.w.held) return;
        e.stopPropagation();
        if (api.vendorBuyChecked(this.npc, index, e.shiftKey)) app.audio.play('sfx_coins', 0.7, 0.08);
      });
      hoverTip(cell, () => this.w.held ? null : itemTip(offer.item, { count: 1, hint: `Buy for ${offer.priceText}`, compare: true, shift: this.w.game.shiftHeld }));
    }
    fill(this.el, titleBar(d.name ?? 'Shop', 'E', () => this.w.closeStation()),
      widget('box', { class: 'small dim' }, `Click to buy, shift-click for ${d.maxBuy}`), grid,
      widget('box', { class: 'row' },
        button('<', () => { this.page--; this.w.refresh(); }, this.page === 0), widget('text', null, `${this.page + 1}/${pages}`),
        button('>', () => { this.page++; this.w.refresh(); }, this.page >= pages - 1), widget('text', { class: 'grow' }),
        d.junk > 0 ? moneyView(d.junk) : null,
        button('Sell junk', () => { api.vendorSellJunk(this.npc); app.audio.play('sfx_coins', 0.7, 0.08); }, d.junk <= 0)));
    return true;
  }
}

// =============================================================================================================
// trade
// =============================================================================================================

interface TradeData {
  open: boolean; partner: string; revision: number; mineAccepted: boolean; theirsAccepted: boolean; myCopper: number; theirCopper: number;
  canAccept: boolean; acceptIn: number; maxSlots: number; mine: { slot: number; item: number; count: number }[]; theirs: Stack[];
}

export class TradeWindow {
  readonly el = widget('box', { class: 'panel window' });
  private readonly money = widget('input', { class: 'field', placeholder: '2s 50c', maxLength: 20, style: { width: `56px` } });
  open = false;

  constructor(private readonly w: Windows) {
    this.el.addEventListener('mousedown', e => {
      if (e.button !== 0 || !this.w.held) return;
      e.stopPropagation();
      const held = this.w.held;
      this.w.setHeld(null); // a stack picked up in the inventory and dropped here is offered
      api.tradeOffer(held.c, held.i);
    });
    this.money.addEventListener('keydown', e => {
      e.stopPropagation();
      if (e.key === 'Enter') this.setMoney();
      if (e.key === 'Escape') this.money.blur();
    });
  }

  private setMoney(): void {
    const error = api.tradeMoney(this.money.value);
    if (error) api.notice('error', error);
    else { this.money.value = ''; this.money.blur(); }
  }

  offerHint(s: Stack): string { return s.bound ? 'Soulbound: can\'t be traded' : 'Shift-click to offer'; }

  render(): boolean {
    const d = api.trade() as TradeData;
    this.open = d.open;
    if (!d.open) return false;
    const grid = (stacks: (Stack & { slot?: number })[], mine: boolean) => {
      const g = widget('box', { class: 'grid', style: { gridTemplateColumns: 'repeat(4, auto)' } });
      for (let i = 0; i < 8; i++) {
        const s = stacks[i];
        const el = slotEl(s ?? null);
        if (!s) el.style.opacity = '0.6';
        else if (mine) {
          el.addEventListener('mousedown', e => { if (e.button === 0 && !this.w.held) { e.stopPropagation(); api.tradeRemove(s.slot!); } });
          hoverTip(el, () => this.w.held ? null : itemTip(s.item, { count: s.count, hint: 'Click to take back' }));
        } else hoverTip(el, () => this.w.held ? null : itemTip(s.item, { bound: s.bound, count: s.count, compare: true, shift: this.w.game.shiftHeld }));
        g.append(el);
      }
      return g;
    };
    const moneyLine = (c: number) => c > 0 ? moneyView(c) : widget('text', { class: 'dim small' }, 'no money');
    const acc = (yes: boolean, who: string) => widget('box', { class: 'small', style: { color: yes ? '#78dc78' : 'var(--dim)' } }, yes ? `${who} accepted` : 'Not accepted');
    const focused = focus.current === this.money;
    fill(this.el, 
      titleBar(`Trading with ${d.partner}`, 'Esc cancels', () => api.tradeCancel()),
      widget('box', { class: 'row', style: { alignItems: 'flex-start', gap: `20px` } },
        widget('box', { class: 'col' }, widget('box', { class: 'small' }, 'Your offer'), grid(d.mine.map(m => ({ ...m, bound: false })), true), moneyLine(d.myCopper), acc(d.mineAccepted, 'You')),
        widget('box', { class: 'col' }, widget('box', { class: 'small' }, d.partner), grid(d.theirs, false), moneyLine(d.theirCopper), acc(d.theirsAccepted, d.partner))),
      widget('box', { class: 'row' }, this.money, button('Set', () => this.setMoney()), widget('text', { class: 'grow' }),
        button(d.mineAccepted ? 'Accepted' : 'Accept', () => api.tradeAccept(), !d.canAccept), button('Cancel', () => api.tradeCancel())));
    if (focused) this.money.focus();
    return true;
  }
}

export { formatMoney };
